import { OrderedCallConnection } from './direct-call-events';
import type {
  HumanConversationApi,
  HumanConversationNotifications,
} from './direct-call-events';
import type {
  DirectCallConnection,
  DirectCallTransport,
  DirectCallThread,
} from './direct-call-session';

export interface HumanCallSetupApi extends HumanConversationApi {
  listThreads?(
    appId: string,
    signal: AbortSignal
  ): Promise<readonly DirectCallThread[]>;
  create(appId: string, signal: AbortSignal): Promise<string>;
  setMembers(
    id: string,
    appId: string,
    members: readonly string[],
    signal: AbortSignal
  ): Promise<readonly string[]>;
}

/** Shared web/native setup; no claimed user, role, or Agent identity on the wire. */
export class HumanCallTransport implements DirectCallTransport {
  constructor(
    private readonly appId: string,
    private readonly api: HumanCallSetupApi,
    private readonly notifications: () => HumanConversationNotifications
  ) {}

  async listThreads(signal: AbortSignal): Promise<readonly DirectCallThread[]> {
    if (signal.aborted) throw new Error('Call setup cancelled');
    if (!this.api.listThreads || !this.appId.trim())
      throw new Error('Thread list unavailable');
    const threads = await this.api.listThreads(this.appId, signal);
    if (signal.aborted) throw new Error('Call setup cancelled');
    return threads;
  }

  async resume(id: string, signal: AbortSignal): Promise<DirectCallConnection> {
    if (!/^[a-zA-Z0-9_-]+$/.test(id))
      throw new Error('Invalid conversation id');
    // Never trust cached picker membership or change members while resuming.
    const thread = (await this.listThreads(signal)).find(
      (candidate) => candidate.id === id
    );
    if (!thread) throw new Error('Conversation not available');
    const connection = new OrderedCallConnection(
      id,
      [...thread.members],
      this.api,
      this.notifications(),
      this.membersUpdate(id)
    );
    try {
      await connection.hydrate(signal);
      return connection;
    } catch (error) {
      connection.close();
      throw error;
    }
  }

  async connect(
    members: readonly string[],
    signal: AbortSignal
  ): Promise<DirectCallConnection> {
    const selected = [...members];
    if (
      signal.aborted ||
      !this.appId.trim() ||
      !selected.length ||
      selected.length > 20 ||
      selected.some((member) => typeof member !== 'string' || !member.trim()) ||
      new Set(selected).size !== selected.length
    )
      throw new Error('Invalid call setup');
    const id = await this.api.create(this.appId, signal);
    if (signal.aborted) throw new Error('Call setup cancelled');
    const confirmed = await this.api.setMembers(
      id,
      this.appId,
      selected,
      signal
    );
    if (signal.aborted) throw new Error('Call setup cancelled');
    if (
      confirmed.length !== selected.length ||
      new Set(confirmed).size !== selected.length ||
      confirmed.some((member) => !selected.includes(member))
    )
      throw new Error('Conversation membership was not confirmed');
    const notifications = this.notifications();
    if (signal.aborted) {
      notifications.close();
      throw new Error('Call setup cancelled');
    }
    return new OrderedCallConnection(
      id,
      [...confirmed],
      this.api,
      notifications,
      this.membersUpdate(id)
    );
  }

  /** Story 35511: the owner's mid-call PUT members, scoped to this app. */
  private membersUpdate(id: string) {
    return (members: readonly string[], signal: AbortSignal) =>
      this.api.setMembers(id, this.appId, members, signal);
  }
}
